import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';

import PoweredByApruv from '../common/PoweredByApruv';
import {
  CompanyBrandProfile,
  brooksBrandProfile,
  getCompanyBrandCssVars,
} from '../../branding/companyBrand';

interface BrandedPreviewShellProps {
  brand?: CompanyBrandProfile;
  children: React.ReactNode;
  previewLabel: string;
  statusLabel?: string;
}

const BrandedPreviewShell: React.FC<BrandedPreviewShellProps> = ({
  brand = brooksBrandProfile,
  children,
  previewLabel,
  statusLabel = 'Preview',
}) => {
  return (
    <Box
      className="company-branded-preview"
      style={getCompanyBrandCssVars(brand)}
      sx={{
        bgcolor: 'var(--cx-brand-bg)',
        color: 'var(--cx-brand-text)',
        minHeight: '100vh',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 1.5, sm: 3 },
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 460, mx: 'auto', minHeight: 'calc(100vh - 24px)' }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.5 }}>
          <IconButton
            aria-label="Back to ratings"
            component={RouterLink}
            to="/ratings"
            sx={{
              bgcolor: 'var(--cx-brand-surface)',
              border: '1px solid',
              borderColor: 'var(--cx-brand-border)',
              height: 40,
              width: 40,
              '&:hover': { bgcolor: 'var(--color-gray-100)' },
            }}
          >
            <ArrowBackIosNewRoundedIcon sx={{ fontSize: 17 }} />
          </IconButton>

          <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1, minWidth: 0 }}>
            <Avatar
              sx={{
                bgcolor: 'var(--cx-brand-primary-dark)',
                color: 'var(--color-base-white)',
                fontSize: 12,
                fontWeight: 800,
                height: 32,
                width: 32,
              }}
            >
              {brand.initials}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap>
                {brand.companyName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }} noWrap>
                {previewLabel}
              </Typography>
            </Box>
          </Box>

          <Chip
            label={statusLabel}
            size="small"
            sx={{
              bgcolor: 'var(--cx-brand-primary-soft)',
              color: 'var(--cx-brand-primary-dark)',
              fontWeight: 800,
            }}
          />
        </Box>

        {children}

        <Box sx={{ flexGrow: 1 }} />
        <PoweredByApruv />
      </Stack>
    </Box>
  );
};

export default BrandedPreviewShell;
